import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'argenergia_consent_v1';

function gtag() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
}

function applyConsent(consent) {
  window.dataLayer = window.dataLayer || [];
  const payload = {
    ad_storage: consent.ads ? 'granted' : 'denied',
    ad_user_data: consent.ads ? 'granted' : 'denied',
    ad_personalization: consent.ads ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    personalization_storage: consent.personalization ? 'granted' : 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  };
  window.dataLayer.push(['consent', 'update', payload]);
  window.dataLayer.push({ event: 'cookie_consent_update', consent: payload });
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    ads: false,
    personalization: false,
  });

  // On mount: read stored consent, apply it, or show banner
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw);
        setPrefs((p) => ({ ...p, ...stored }));
        applyConsent(stored);
      } else {
        setVisible(true);
      }
    } catch (e) {
      setVisible(true);
    }

    // Expose a manual re-open (from footer link, etc.)
    window.__openCookiePrefs = () => {
      setVisible(true);
      setShowPrefs(true);
    };
  }, []);

  const accept = (choice) => {
    const consent = {
      necessary: true,
      analytics: choice.analytics,
      ads: choice.ads,
      personalization: choice.personalization,
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(consent)); } catch (e) {}
    applyConsent(consent);
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => accept({ analytics: true, ads: true, personalization: true });
  const rejectAll = () => accept({ analytics: false, ads: false, personalization: false });
  const savePrefs = () => accept({ analytics: prefs.analytics, ads: prefs.ads, personalization: prefs.personalization });

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" aria-label="cookies" data-testid="cookie-banner">
      <h3 className="cookie-title">{t('cookie.title')}</h3>
      <p className="cookie-desc">{t('cookie.desc')}</p>

      {showPrefs && (
        <div className="cookie-prefs">
          <div className="pref-row">
            <div>
              <p className="pref-title">{t('cookie.categories.necessary.title')}</p>
              <p className="pref-desc">{t('cookie.categories.necessary.desc')}</p>
            </div>
            <div className="toggle on disabled" aria-disabled />
          </div>
          <div className="pref-row">
            <div>
              <p className="pref-title">{t('cookie.categories.analytics.title')}</p>
              <p className="pref-desc">{t('cookie.categories.analytics.desc')}</p>
            </div>
            <button className={`toggle ${prefs.analytics ? 'on' : ''}`} onClick={() => toggle('analytics')} data-testid="pref-analytics" aria-pressed={prefs.analytics} />
          </div>
          <div className="pref-row">
            <div>
              <p className="pref-title">{t('cookie.categories.ads.title')}</p>
              <p className="pref-desc">{t('cookie.categories.ads.desc')}</p>
            </div>
            <button className={`toggle ${prefs.ads ? 'on' : ''}`} onClick={() => toggle('ads')} data-testid="pref-ads" aria-pressed={prefs.ads} />
          </div>
          <div className="pref-row">
            <div>
              <p className="pref-title">{t('cookie.categories.personalization.title')}</p>
              <p className="pref-desc">{t('cookie.categories.personalization.desc')}</p>
            </div>
            <button className={`toggle ${prefs.personalization ? 'on' : ''}`} onClick={() => toggle('personalization')} data-testid="pref-personalization" aria-pressed={prefs.personalization} />
          </div>
        </div>
      )}

      <div className="cookie-actions">
        <button className="cookie-btn primary" onClick={acceptAll} data-testid="cookie-accept-all">{t('cookie.acceptAll')}</button>
        <button className="cookie-btn secondary" onClick={rejectAll} data-testid="cookie-reject-all">{t('cookie.rejectAll')}</button>
        {!showPrefs ? (
          <button className="cookie-btn ghost" onClick={() => setShowPrefs(true)} data-testid="cookie-manage">{t('cookie.manage')}</button>
        ) : (
          <button className="cookie-btn ghost" onClick={savePrefs} data-testid="cookie-save">{t('cookie.savePrefs')}</button>
        )}
      </div>
    </div>
  );
}
